import { Component, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';

import { filter, map, startWith } from 'rxjs/operators';

import { ENVIRONMENT } from '@app/constants/environment.token';
import { AuthService } from '@app/services/auth.service';
import { UtilsService } from '@app/services/utils.service';

import { VERSION } from '@env/version';

type HeaderMode = 'hamburger' | 'back-fixed' | 'back-history';

interface HeaderConfig {
    mode: HeaderMode;
    backPath?: string;
}

/**
 * Header organism — fixed top navigation bar.
 * Includes logo, desktop nav links, icon links (WP), and mobile slide-out drawer.
 *
 * @example
 * <mst-header />
 * <mst-header [backButtonLocation]="'/training'" />
 * <mst-header [basicBackButton]="true" />
 */
@Component({
    selector: 'mst-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [RouterLink],
})
export class HeaderComponent {
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly env = inject(ENVIRONMENT);
    private readonly utilsService = inject(UtilsService);

    /**
     * True when the app is running in PWA standalone mode (installed on home screen).
     * Used to redirect WordPress links to internal Angular routes.
     */
    readonly isStandaloneMode = this.utilsService.isInStandaloneMode();

    /** Whether the mobile side-drawer is open. */
    readonly sideMenuIsOpen = signal(false);

    /**
     * Header configuration derived from the active leaf route's data.
     * Defaults to hamburger mode when no route data is present.
     */
    readonly headerConfig = toSignal(
        this.router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            startWith(null),
            map(() => {
                let route = this.router.routerState.snapshot.root;
                while (route.firstChild) route = route.firstChild;
                return (route.data['header'] as HeaderConfig | undefined) ?? { mode: 'hamburger' as const };
            }),
        ),
        { initialValue: { mode: 'hamburger' } as HeaderConfig },
    );

    /** WordPress base URL for external links. */
    readonly wpUrl = this.env.wpUrl;

    /** App version from package.json + git info. */
    readonly version = VERSION.version;

    /** Resolved current URL, updated on every Navigation event. */
    readonly currentUrl = toSignal(
        this.router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            map((e) => (e as NavigationEnd).urlAfterRedirects),
            startWith(this.router.url),
        ),
        { initialValue: this.router.url },
    );

    /** True when a user is authenticated. */
    readonly isLoggedIn = computed(() => this.authService.currentUser() !== null);

    /**
     * Returns true when the given route segment matches the current URL.
     * @param segment Route segment to match, or null to check for "My Area" active state.
     * @param isEqual When true, performs an exact match instead of includes.
     */
    isActive(segment: string | null, isEqual = false): boolean {
        const url = this.currentUrl() ?? '';

        if (!segment) {
            return !url.includes('programs') && !url.includes('shop') && !url.includes('faq') && !url.includes('msb4you');
        }

        return isEqual ? url === `/${segment}` : url.includes(segment);
    }

    /** Navigate to an internal app route and close the mobile drawer. */
    navigateTo(path: string): void {
        this.closeSideMenu();
        void this.router.navigateByUrl(path);
    }

    /** Navigate to a WordPress URL and close the mobile drawer. */
    navigateToWp(relPath: string): void {
        this.closeSideMenu();
        window.location.href = `${this.wpUrl}${relPath}`;
    }

    /**
     * Handles clicks on links that may be external WordPress URLs.
     * In PWA standalone mode, navigates to the internal Angular route and closes
     * the mobile drawer instead of following the href.
     *
     * @param internalPath - Angular route to navigate to in standalone mode.
     * @param event - Click event used to prevent default href navigation.
     */
    navigateWpOrInternal(internalPath: string, event: MouseEvent): void {
        if (this.isStandaloneMode) {
            event.preventDefault();
            this.closeSideMenu();
            void this.router.navigate([internalPath]);
        }
    }

    toggleSideMenu(): void {
        this.sideMenuIsOpen.update((open) => !open);
    }

    closeSideMenu(): void {
        this.sideMenuIsOpen.set(false);
    }

    goBack(): void {
        window.history.back();
    }

    logout(): void {
        this.authService.logout().subscribe();
    }
}
